/**
 * The white left panel: brand, the six sections, and sign in / sign up.
 *
 * Section links come from DATASETS, so this file never needs editing when a
 * section is added.
 */

import { ShieldCheck, SquareKanban } from "lucide-react";
import { NavLink } from "react-router-dom";

import { DATASETS } from "../../lib/datasets";
import { useViewer } from "../../hooks/useViewer";
import { SidebarAuth } from "./SidebarAuth";

export function Sidebar() {
  const { isAdmin } = useViewer();

  return (
    <aside className="flex h-full w-[12.8rem] flex-none flex-col border-r border-slate-200 bg-white">
      <div className="px-3 py-3">
        <NavLink to="/app/funding" className="flex items-center gap-2.5">
          <span className="flex size-6 items-center justify-center rounded-md bg-brand">
            <span className="size-1.5 rounded-sm bg-white" />
          </span>
          <span className="min-w-0">
            <span className="block text-base font-semibold leading-tight text-slate-950">
              Cone
            </span>
            <span className="block truncate text-[11px] leading-tight text-slate-500">
              Proposal software
            </span>
          </span>
        </NavLink>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-3" aria-label="Sections">
        <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Sections
        </p>

        <ul className="flex flex-col gap-0.5 pb-3">
          {DATASETS.map((dataset) => {
            const Icon = dataset.icon;
            return (
              <li key={dataset.id}>
                <NavLink
                  to={dataset.path}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-2.5 py-2 transition-colors ${
                      isActive
                        ? "bg-slate-100 text-slate-950"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={`size-[17px] shrink-0 ${isActive ? "text-brand" : "text-slate-400"}`}
                        strokeWidth={2}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {dataset.title}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {dataset.subtitle}
                        </span>
                      </span>
                    </>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <p className="px-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
          Workspace
        </p>
        <ul className="flex flex-col gap-0.5">
          <li>
            <NavLink
              to="/app/tasks"
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-2.5 py-2 transition-colors ${
                  isActive
                    ? "bg-slate-100 text-slate-950"
                    : "text-slate-700 hover:bg-slate-100 hover:text-slate-950"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <SquareKanban
                    className={`size-[17px] shrink-0 ${isActive ? "text-brand" : "text-slate-400"}`}
                    strokeWidth={2}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">Tasks</span>
                    <span className="block truncate text-xs text-slate-500">
                      Your kanban board
                    </span>
                  </span>
                </>
              )}
            </NavLink>
          </li>
        </ul>

        {isAdmin && (
          <p className="mt-5 flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
            <ShieldCheck className="size-3.5" />
            Admin mode — you can edit rows
          </p>
        )}
      </nav>

      <div className="border-t border-slate-200 px-3 py-4">
        <SidebarAuth />
      </div>
    </aside>
  );
}
